import { useEffect, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { AgentProfile, AgentLoad } from '@/types';

/**
 * How often the own-load figure is refreshed while the agent hub is connected.
 * OP-303/OP-331 lesson — no idle polling storm: this timer runs ONLY while
 * `connected` is true and is torn down the moment the hub drops (or the
 * component unmounts), so a disconnected/backgrounded console never polls.
 */
export const LOAD_POLL_INTERVAL_MS = 15_000;

export interface AgentLoadBadgeState {
  /** Resolved 0–10 cap; null until the profile GET returns. */
  cap: number | null;
  /** Current open-conversation count; null until the first my-load GET. */
  load: number | null;
}

/**
 * useAgentLoad — reads the caller's OWN resolved cap (once) and polls their own
 * current load while the hub is connected, for the read-only "load / cap" badge
 * (ADR-016 §D9).
 *
 *  - Cap comes from GET /api/agent-profiles/{agentId} and rarely changes, so it
 *    is fetched a single time per agentId (an admin editing it is out of band).
 *  - Load comes from GET /api/escalation/my-load and is polled on a light cadence
 *    ONLY while `connected` — the disconnected/reconnecting console does not poll
 *    (OP-303/331). Failures keep the last known value rather than flap the badge.
 *
 * @param agentId  the caller's own agent id (JWT `sub`); null ⇒ no fetch.
 * @param connected true only while the agent hub is in the Connected state.
 */
export function useAgentLoad(
  agentId: string | null | undefined,
  connected: boolean,
): AgentLoadBadgeState {
  const [cap, setCap] = useState<number | null>(null);
  const [load, setLoad] = useState<number | null>(null);

  // Cap — fetched once per agent. Independent of hub connection.
  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    axiosClient
      .get<AgentProfile>(API.AGENTS.PROFILE(agentId))
      .then(({ data }) => {
        if (!cancelled) setCap(data.maxConcurrentConversations);
      })
      .catch(() => {
        /* leave cap null — the badge stays hidden rather than showing a guess */
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // Load — polled ONLY while connected. The effect returns early (no timer,
  // no fetch) when disconnected, so a dropped hub means zero polling.
  useEffect(() => {
    if (!agentId || !connected) return;
    let cancelled = false;
    const fetchLoad = async () => {
      try {
        const { data } = await axiosClient.get<AgentLoad>(API.ESCALATION.MY_LOAD);
        if (!cancelled) setLoad(data.openConversations);
      } catch {
        /* keep the last known load — a transient blip should not flap the badge */
      }
    };
    fetchLoad();
    const timer = setInterval(fetchLoad, LOAD_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [agentId, connected]);

  return { cap, load };
}
